/* eslint-disable react/no-danger-with-children */
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import "../../../index.css";
import useUpdateDomTitle from "../../../../utils/updateDomTitle";
import WelcomeScreen from "../../../common/WelcomeScreen";
import useScroller from "../../../../utils/Scroller";
import useLanguage from "../../../../utils/useLanguage";
import { useAdjustBackdropHeight } from "../../../backdrop";
import EmailBackdrop from "./_internal/EmailBackdrop";
import EmailHero from "./_internal/EmailHero";
import EmailTemplateRow from "./_internal/EmailTemplateRow";
import { isLight } from "../../../../utils/Color";
import ContactDetails from "../../../common/contactDetails";
import { EMAIL_LIGHT_THRESHOLD, omitColors, PHONE_TYPE_BY_ICON } from "./_internal/constants";
import { buildMailtoUrl } from "./_internal/mailto";
import AOS from "aos";
import "aos/dist/aos.css";

let Email = ({ qrData }) => {
  const [welcomeScreen, showWelcomeScreen] = useState(false);
  const data = qrData;
  const { t } = useLanguage();

  useEffect(() => {
    AOS.init({
      duration: 2000,
    });

    const d = setTimeout(() => {
      showWelcomeScreen(false);
      if (qrData) qrData.screen = false;
    }, 3000);

    return () => clearTimeout(d);
  }, [qrData, qrData?.screen]);

  const colors = data?.colors ?? {};
  const basicInformation = data?.basic_information ?? {};
  const contactDetails = data?.contact_details ?? {};

  useUpdateDomTitle(
    contactDetails.contact_name ?? qrData?.contactName,
    colors.primary ?? qrData?.primaryColor,
  );

  useEffect(() => {
    showWelcomeScreen(Boolean(qrData?.screen));
  }, [qrData?.screen]);

  useScroller(qrData);

  const backdropElement = useRef(null);
  const backdropEndElement = useRef(null);
  const backdropEndFallbackElement = useRef(null);

  /**
   * Only templates with both a recipient and a label are actionable, so
   * incomplete ones are skipped instead of rendering a dead row.
   */
  const templates = useMemo(
    () =>
      (data?.email_templates ?? []).filter(
        (template) => template?.title && template?.email_address,
      ),
    [data?.email_templates],
  );

  /**
   * ContactDetails is shared with the other QR types, so the rows are handed
   * over in the prop shape it already expects.
   */
  const phones = useMemo(
    () =>
      (contactDetails.phones ?? [])
        .map((phone) => ({
          label: phone.label ?? "",
          type: PHONE_TYPE_BY_ICON[phone.icon] ?? "other",
          number: phone.number,
        })),
    [contactDetails.phones],
  );

  const emails = useMemo(
    () =>
      (contactDetails.emails ?? [])
        .map((entry) => ({ title: entry.label ?? "", email: entry.email })),
    [contactDetails.emails],
  );

  const websites = useMemo(
    () =>
      (contactDetails.websites ?? [])
        .map((website) => ({
          title: website.label ?? "",
          website: website.url,
        })),
    [contactDetails.websites],
  );

  const showContactDetails = useMemo(
    () =>
      Boolean(
        contactDetails.contact_name ||
        phones.length ||
        emails.length ||
        websites.length
      ),
    [contactDetails.contact_name, phones, emails, websites],
  );

  useAdjustBackdropHeight(
    backdropElement,
    backdropEndElement,
    backdropEndFallbackElement,
    data,
    templates.length,
  );

  if (welcomeScreen) return <WelcomeScreen screen={qrData?.screen} />;

  const useLightTextColor = !isLight(colors.primary, omitColors, EMAIL_LIGHT_THRESHOLD);

  const textFont = data?.fonts?.texts ?? data?.font_text;
  const titleFont = data?.fonts?.title ?? data?.font_title;
  const fontFamily = `'${textFont}'`;

  return (
    <div className="relative flex flex-col justify-center items-center zoom-block mb-6">
      <div className="absolute top-0 z-0 flex w-full items-center justify-center overflow-x-clip">
        <EmailBackdrop
          bgColor={colors.primary}
          backdropElementRef={backdropElement}
        />
      </div>

      <div className="relative mt-[4.125rem] flex w-full max-w-[700px] flex-col items-center justify-start px-[16px]">
        <div className="relative w-full">
          <EmailHero
            title={basicInformation.title}
            description={basicInformation.description}
            font_title={titleFont}
            image={basicInformation.image}
            useLightTextColor={useLightTextColor}
          />

          <div
            ref={backdropEndFallbackElement}
            aria-hidden="true"
            className="pointer-events-none absolute bottom-[-40px] left-0 h-0 w-0"
          />
        </div>

        <div className="mt-11 flex w-full flex-col gap-2.5">
          {templates.map((template, index) => (
            <EmailTemplateRow
              key={index}
              backdropEndElement={index === 0 ? backdropEndElement : undefined}
              icon={template.icon}
              title={template.title}
              fontFamily={fontFamily}
              color={colors.secondary}
              href={buildMailtoUrl(
                template.email_address,
                template.subject,
                template.body,
              )}
            />
          ))}
        </div>

        {showContactDetails && (
          <div className="mt-10 w-full">
            <ContactDetails
              showContactTitle
              contactName={contactDetails.contact_name}
              fontText={fontFamily}
              phones={phones}
              emails={emails}
              websites={websites}
              t={t}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Email;
